/**
 * Building blocks shared by the Grade 5–8 templates (grade05.ts … grade08.ts).
 *
 * Scoped to this grade range (hence the file name) so it never collides with
 * helpers owned by other grade files. Everything here is pure; the only
 * randomness is the Rng passed in, so templates stay deterministic.
 */
import { int } from '../../math/rational.ts';
import type { Rng } from '../../math/rng.ts';
import type { GeneratedProblemCore, MathMarkup } from '../../types.ts';

export type Instruction = NonNullable<GeneratedProblemCore['instruction']>;

/** Difficulty clamped to [0, 1] (NaN counts as 0) so range maths never runs off the ends. */
function clampDifficulty(difficulty: number): number {
  return Number.isNaN(difficulty) ? 0 : Math.min(1, Math.max(0, difficulty));
}

/** A whole-number range bound interpolated from `easy` (difficulty 0) to `hard` (difficulty 1). */
export function span(difficulty: number, easy: number, hard: number): number {
  return Math.round(easy + (hard - easy) * clampDifficulty(difficulty));
}

/** Non-zero whole number with magnitude in [lo, hi] and a random sign (lo ≥ 1). */
export function signedInt(rng: Rng, lo: number, hi: number): number {
  return rng.sign() * rng.int(lo, hi);
}

/** Letters for a single unknown. Variety keeps letters feeling like placeholders, not "always x". */
export const UNKNOWNS = ['x', 'n', 'm', 'p', 'k', 'a'] as const;

/** Letter pairs for two-variable expressions (substitution, collecting like terms). */
export const LETTER_PAIRS = [
  ['a', 'b'],
  ['x', 'y'],
  ['m', 'n'],
  ['p', 'q'],
] as const;

/** One term of a sum: `coef·v`, or a plain constant when `v` is omitted. `v` may be any markup (`x`, `x^{2}`, `ab`). */
export interface Term {
  coef: number;
  v?: MathMarkup;
}

function termBody(magnitude: number, v: MathMarkup | undefined): MathMarkup {
  if (v === undefined) return `${magnitude}`;
  return magnitude === 1 ? v : `${magnitude}${v}`;
}

/**
 * Render a sum of terms as sign-correct markup:
 * `[{ coef: 3, v: 'x' }, { coef: -2 }]` → `3x - 2`.
 * Zero terms are dropped (never `x + 0` or `0x`), coefficients ±1 are implicit
 * (`x`, `-x`, never `1x`), and a negative term is written `- 2`, never `+ -2`.
 */
export function linear(terms: readonly Term[]): MathMarkup {
  let out = '';
  for (const term of terms) {
    if (term.coef === 0) continue;
    out += out ? continuation(term) : `${term.coef < 0 ? '-' : ''}${termBody(Math.abs(term.coef), term.v)}`;
  }
  return out || '0';
}

/**
 * One term written as the continuation of a sum, with the same rules as
 * `linear`: `{ coef: 5, v: 'b' }` → ` + 5b`, `{ coef: -1, v: 'b' }` → ` - b`,
 * `{ coef: -3 }` → ` - 3`, and a zero term → `` (nothing).
 */
export function continuation(term: Term): MathMarkup {
  if (term.coef === 0) return '';
  return `${term.coef < 0 ? ' - ' : ' + '}${termBody(Math.abs(term.coef), term.v)}`;
}

/** Coefficient written in front of a letter or bracket: 1 → '', −1 → '-', otherwise the number. */
export function coefficient(c: number): string {
  if (c === 1) return '';
  if (c === -1) return '-';
  return `${c}`;
}

/**
 * A blank written after a sign, the conventional way. A negative value is shown
 * as ` - □` and the player types its size, so a completed line never reads
 * `10a + −20` (content rule: no `+ -3`).
 */
export interface SignedBlank {
  /** ` + ` or ` - ` (spaced), written in front of `\box`. */
  sign: ' + ' | ' - ';
  /** What the player types: the value's size. */
  answer: number;
  /**
   * What a learner types in this box when their working gives the signed value
   * `signed` — maps a misconception's result into the box's form, so the sign
   * slip stays the opposite of the answer. `null` passes through.
   */
  fill(signed: number | null): number | null;
}

/** Sign and answer for a blank whose signed value is `value` (see SignedBlank). */
export function signedBlank(value: number): SignedBlank {
  const negative = value < 0;
  return {
    sign: negative ? ' - ' : ' + ',
    answer: Math.abs(value),
    fill: (signed) => (signed === null ? null : (negative ? -signed : signed) + 0), // `+ 0` normalizes -0
  };
}

/** `a ÷ b` when it is a whole number, else null — for misconceptions that only make sense when they land on an integer. */
export function exactDiv(a: number, b: number): number | null {
  if (b === 0 || a % b !== 0) return null;
  return a / b + 0; // `+ 0` normalizes -0
}

/**
 * Package a problem whose answer is a whole number (negative allowed from Grade 7).
 *
 * `distractors` lists every misconception the template models; `null` means
 * "does not apply to this instance" (e.g. the wrong working doesn't give a
 * whole number). Cleaning happens here so templates don't special-case
 * coincidences: non-integers, disallowed negatives, duplicates and the answer
 * itself are dropped. If fewer than two survive, arithmetic slips of ±1/±2
 * top the list up, so there are always at least two.
 */
export function integerProblem(spec: {
  prompt: MathMarkup;
  answer: number;
  distractors: readonly (number | null)[];
  allowNegative: boolean;
  instruction?: Instruction;
}): GeneratedProblemCore {
  const { prompt, answer, distractors, allowNegative, instruction } = spec;
  const kept: number[] = [];
  const consider = (v: number | null) => {
    if (v === null || !Number.isSafeInteger(v) || v === answer || kept.includes(v)) return;
    if (v < 0 && !allowNegative) return;
    kept.push(v + 0);
  };
  for (const v of distractors) consider(v);
  for (const v of [answer + 1, answer - 1, answer + 2, answer - 2]) if (kept.length < 2) consider(v);
  return {
    prompt,
    ...(instruction ? { instruction } : {}),
    answer: int(answer),
    answerKind: 'integer',
    distractors: kept.map((v) => int(v)),
  };
}
